import { css, html } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { colors, events, DropdownColorPicker } from '@omegagrid/core';
import { CellModel, Renderer, editors } from '@omegagrid/grid';
import { formatColor } from '../viewOptions';

/** color swatch with the hex value, same look as the colorpicker label */
export const colorCellRenderer: Renderer = (div, cell) => {
	const color = cell.value ? formatColor(cell.value as string) : null;
	div.textContent = color ?? '';
	div.style.background = color ?? '';
	try {
		div.style.color = color ? colors.contrastColor(color) : '';
	} catch {
		div.style.color = '';
	}
	return div;
};

/**
 * Grid cell editor picking a color, used by the series form of the chart
 * editor. The dropdown opens right away and appends its popup to the document
 * body, so it is not clipped by the grid/panel overflow. The cell value is
 * the color in the view options convention - a hex string without the
 * leading # - or null for no color (the clear cross of the picker).
 */
@customElement('rp-grid-coloreditor')
export class ColorCellEditor extends editors.BaseEditor {

	static styles = [editors.BaseEditor.baseStyles, css`
		og-dropdown-colorpicker {
			width: 100%;
			height: 100%;
		}
	`];

	@query('og-dropdown-colorpicker')
	dropdown: DropdownColorPicker;

	private currentValue: string;

	async activate(cell: CellModel) {
		super.activate(cell);
		this.currentValue = (cell.value as string) ?? null;
		await this.dropdown.updateComplete;
		this.dropdown.value = this.currentValue;
		this.dropdown.open();
	}

	getValue(): string {
		return this.currentValue;
	}

	setValue(value: unknown) {
		this.currentValue = value == null || value === ''
			? null
			: value.toString().replace(/^#+/g, '');
		if (this.dropdown) this.dropdown.value = this.currentValue;
	}

	/** change fires on a picked swatch, a committed text input and the clear cross */
	_onChange = (e: events.ChangeEvent) => {
		this.setValue(e.value);
		this.commit();
		this.close();
	}

	close() {
		this.dropdown?.close();
		super.close();
	}

	render = () => html`
		<og-dropdown-colorpicker @change="${this._onChange}"></og-dropdown-colorpicker>
	`;

}
